export type WidgetConversationMode =
  | 'text-only'
  | 'text-first'
  | 'voice-first'
  | 'voice-only';

/** Public widget.live settings; branding continues to come from widget.config. */
export interface VoiceConfig {
  /** Defaults to text-only. Voice-first modes wait for an explicit Start click. */
  mode?: WidgetConversationMode;
  supportsVideoInput?: boolean;
  supportsScreenShare?: boolean;
  /** Optional override for deployments hosting the voice module separately. */
  moduleUrl?: string;
}

/** Treat absent or unrecognised settings as text-only. */
export function conversationMode(config?: VoiceConfig): WidgetConversationMode {
  const mode = config?.mode;
  return mode === 'text-first' || mode === 'voice-first' || mode === 'voice-only'
    ? mode
    : 'text-only';
}
export const hasVoice = (config?: VoiceConfig) =>
  conversationMode(config) !== 'text-only';
export const canSwitchModes = (config?: VoiceConfig) => {
  const mode = conversationMode(config);
  return mode === 'text-first' || mode === 'voice-first';
};

export type VoiceStatus =
  | 'idle'
  | 'loading'
  | 'permissions'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'reconnecting';
export type MediaSource = 'microphone' | 'camera' | 'screen';
export interface TranscriptEntry {
  id: string;
  speaker: 'user' | 'agent';
  text: string;
  final: boolean;
}
export interface VoiceState {
  status: VoiceStatus;
  active: boolean;
  connected: boolean;
  error: string;
  audioBlocked: boolean;
  microphone: boolean;
  camera: boolean;
  screen: boolean;
  canMicrophone: boolean;
  canCamera: boolean;
  canScreen: boolean;
  busy: MediaSource[];
  devices: { deviceId: string; kind: string; label: string }[];
  selectedMicrophone: string;
  selectedCamera: string;
  level: number;
  mediaRevision: number;
  transcript: TranscriptEntry[];
}
export const initialVoiceState = (): VoiceState => ({
  status: 'idle',
  active: false,
  connected: false,
  error: '',
  audioBlocked: false,
  microphone: true,
  camera: false,
  screen: false,
  canMicrophone: false,
  canCamera: false,
  canScreen: false,
  busy: [],
  devices: [],
  selectedMicrophone: '',
  selectedCamera: '',
  level: 0,
  mediaRevision: 0,
  transcript: [],
});
export interface VoiceStartOptions {
  apiBaseUrl: string;
  tenant?: string;
  config?: string;
  agent?: string;
  capabilities: VoiceConfig;
  metadata: {
    user_id: string;
    user_tz: string;
    url: string;
    attributes?: Record<string, unknown>;
    flags?: string[];
  };
}
export interface VoiceController {
  start(options: VoiceStartOptions): Promise<void>;
  stop(): void;
  readAudioLevel(): number;
  toggle(source: MediaSource): Promise<void>;
  refreshDevices(kind?: 'audioinput' | 'videoinput'): Promise<void>;
  selectDevice(kind: 'audioinput' | 'videoinput', id: string): Promise<void>;
  startAudio(): Promise<void>;
  attachPreview(
    element: HTMLVideoElement,
    source: 'camera' | 'screen'
  ): () => void;
}
